/**
 * The cell tree: a quadtree of coordinate frames ("portals") that gives the
 * canvas effectively unlimited zoom range while keeping every coordinate
 * bounded — the same family of technique used by deep-zoom renderers.
 *
 * - The root cell (id "") covers the whole world; its local space is
 *   [-1, 1] x [-1, 1].
 * - Each cell may have up to 4 children. A child's local space is also
 *   [-1, 1] x [-1, 1] and maps into its parent with
 *   parent = offset(q) + 0.5 * local, i.e. each level halves the scale.
 * - Cell ids are path strings of quadrant digits:
 *   0 = NW (x<0, y<0)  1 = NE (x>=0, y<0)  2 = SW (x<0, y>=0)  3 = SE (x>=0, y>=0)
 *   (canvas convention: y grows downward)
 *
 * Every stroke lives in exactly one cell, in that cell's local coordinates,
 * so coordinates stay within roughly [-1.5, 1.5] no matter how deep you zoom.
 */

import { Mat2D, mul, scaleM, translate } from "./math";

export const QUAD_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [-0.5, -0.5],
  [0.5, -0.5],
  [-0.5, 0.5],
  [0.5, 0.5],
];

/** Maximum depth. 512 levels => 2^512 zoom range, far beyond any device. */
export const MAX_DEPTH = 512;

/** Local space a cell's cached raster covers (margin for strokes that extend
 *  slightly past the [-1,1] border before being promoted to a parent). */
export const CELL_EXTENT = 1.5;

/**
 * Half-size of the drawable top-level paper: strokes reaching the root are
 * clamped here ("like the edge of paper"). The canvas boundary shown when
 * zoomed out must match this, not the [-1,1] cell frame.
 */
export const WORLD_EXTENT = CELL_EXTENT - 0.2;

export interface Cell {
  readonly id: string;
  readonly depth: number;
  /** Cell -> parent transform. */
  readonly localToParent: Mat2D;
  readonly parentId: string | null;
  children: Partial<Record<0 | 1 | 2 | 3, Cell>>;
  /** Strokes/elements in this cell, keyed by layer id. */
  layers: Map<string, { strokes: Map<string, unknown>; elements: Map<string, unknown> }>;
  /** True if this cell or any descendant holds content (used for culling). */
  hasContent: boolean;
}

export const quadrantOf = (p: { x: number; y: number }): 0 | 1 | 2 | 3 => {
  const qx = p.x >= 0 ? 1 : 0;
  const qy = p.y >= 0 ? 1 : 0;
  return (qy * 2 + qx) as 0 | 1 | 2 | 3;
};

const transformForQuad = (q: 0 | 1 | 2 | 3): Mat2D => {
  const [ox, oy] = QUAD_OFFSETS[q]!;
  return mul(translate(ox, oy), scaleM(0.5));
};

export class CellTree {
  readonly root: Cell;

  constructor() {
    this.root = this.makeCell("", null, translate(0, 0));
  }

  private makeCell(id: string, parentId: string | null, localToParent: Mat2D): Cell {
    return {
      id,
      depth: id.length,
      localToParent,
      parentId,
      children: {},
      layers: new Map(),
      hasContent: false,
    };
  }

  get(id: string): Cell {
    if (id === "") return this.root;
    let cell = this.root;
    for (const ch of id) {
      const child = cell.children[Number(ch) as 0 | 1 | 2 | 3];
      if (!child) {
        throw new Error(`Cell path not present: "${id}"`);
      }
      cell = child;
    }
    return cell;
  }

  has(id: string): boolean {
    if (id === "") return true;
    let cell = this.root;
    for (const ch of id) {
      const child = cell.children[Number(ch) as 0 | 1 | 2 | 3];
      if (!child) return false;
      cell = child;
    }
    return true;
  }

  /** Get or create the cell at `id`, creating intermediate cells as needed. */
  ensure(id: string): Cell {
    if (id.length > MAX_DEPTH) {
      throw new Error(`Maximum zoom depth exceeded (${MAX_DEPTH})`);
    }
    let cell = this.root;
    for (const ch of id) {
      const q = Number(ch) as 0 | 1 | 2 | 3;
      let child = cell.children[q];
      if (!child) {
        child = this.makeCell(cell.id + ch, cell.id, transformForQuad(q));
        cell.children[q] = child;
      }
      cell = child;
    }
    return cell;
  }

  getOrCreate(id: string): Cell {
    return this.has(id) ? this.get(id) : this.ensure(id);
  }

  /**
   * Descend one level into the child containing point `p` (in the current
   * cell's local space). Creates the child on demand. Returns the child and
   * the point re-expressed in the child's space.
   */
  descend(cell: Cell, p: { x: number; y: number }): { cell: Cell; point: { x: number; y: number } } {
    if (cell.depth >= MAX_DEPTH) return { cell, point: p };
    const q = quadrantOf(p);
    const child = cell.children[q] ?? this.ensure(cell.id + String(q));
    const [ox, oy] = QUAD_OFFSETS[q]!;
    return { cell: child, point: { x: (p.x - ox) * 2, y: (p.y - oy) * 2 } };
  }

  parentOf(cell: Cell): Cell | null {
    return cell.parentId === null ? null : this.get(cell.parentId);
  }

  /**
   * Transform mapping `from` cell local coordinates to `to` cell local
   * coordinates. Both must exist. Powers of two keep this exact in float64.
   */
  transform(fromId: string, toId: string): Mat2D {
    // Find the common ancestor by walking up the deeper path. Accumulate
    // from→root by prepending each level (root-most transform first).
    let f = fromId;
    let t = toId;
    let fToRoot: Mat2D = translate(0, 0);
    let tToRoot: Mat2D = translate(0, 0);
    // Ascend from both to the same depth as the shallower one.
    const commonLen = Math.min(f.length, t.length);
    while (f.length > commonLen) {
      const cell = this.get(f);
      fToRoot = mul(cell.localToParent, fToRoot);
      f = cell.parentId!;
    }
    while (t.length > commonLen) {
      const cell = this.get(t);
      tToRoot = mul(cell.localToParent, tToRoot);
      t = cell.parentId!;
    }
    // Ascend both until paths meet.
    while (f !== t) {
      const cf = this.get(f);
      const ct = this.get(t);
      fToRoot = mul(cf.localToParent, fToRoot);
      tToRoot = mul(ct.localToParent, tToRoot);
      f = cf.parentId!;
      t = ct.parentId!;
    }
    return mul(invertLocal(tToRoot), fToRoot);
  }

  /**
   * Visit cells that intersect `worldRect` (in the coordinate space of
   * `focusId`), calling `visit(cell, cellRectInFocusSpace)` per cell, and
   * descending only while `shouldDescend(cell, depthDelta)` returns true.
   * Order is depth-first pre-order.
   */
  visit(
    focusId: string,
    worldRect: { x: number; y: number; w: number; h: number },
    shouldDescend: (cell: Cell) => boolean,
    visit: (cell: Cell, rectInFocus: { x: number; y: number; w: number; h: number }) => void,
  ): void {
    const rootRect = this.transformRect(focusId, "", worldRect);
    this.visitFrom(this.root, rootRect, shouldDescend, visit, focusId);
  }

  private visitFrom(
    cell: Cell,
    rectInCell: { x: number; y: number; w: number; h: number },
    shouldDescend: (cell: Cell) => boolean,
    visit: (cell: Cell, rectInFocus: { x: number; y: number; w: number; h: number }) => void,
    focusId: string,
  ): void {
    if (
      rectInCell.x + rectInCell.w < -CELL_EXTENT ||
      rectInCell.x > CELL_EXTENT ||
      rectInCell.y + rectInCell.h < -CELL_EXTENT ||
      rectInCell.y > CELL_EXTENT
    ) {
      return; // no intersection with this cell's extent
    }
    visit(cell, rectInCell);
    if (!shouldDescend(cell)) return;
    for (const q of [0, 1, 2, 3] as const) {
      const child = cell.children[q];
      if (!child) continue;
      const [ox, oy] = QUAD_OFFSETS[q]!;
      const childRect = {
        x: (rectInCell.x - ox) * 2,
        y: (rectInCell.y - oy) * 2,
        w: rectInCell.w * 2,
        h: rectInCell.h * 2,
      };
      this.visitFrom(child, childRect, shouldDescend, visit, focusId);
    }
  }

  /** Map a rect from `fromId` space to `toId` space. */
  private transformRect(
    fromId: string,
    toId: string,
    r: { x: number; y: number; w: number; h: number },
  ): { x: number; y: number; w: number; h: number } {
    const m = this.transform(fromId, toId);
    const corners = [
      { x: r.x, y: r.y },
      { x: r.x + r.w, y: r.y },
      { x: r.x, y: r.y + r.h },
      { x: r.x + r.w, y: r.y + r.h },
    ].map((p) => {
      const q = { x: m.a * p.x + m.c * p.y + m.e, y: m.b * p.x + m.d * p.y + m.f };
      return q;
    });
    const xs = corners.map((p) => p.x);
    const ys = corners.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY };
  }
}

function invertLocal(m: Mat2D): Mat2D {
  // localToParent is always translate(off) ∘ scale(0.5): invert analytically.
  // parent = off + 0.5 * local  =>  local = 2 * (parent - off)
  const det = m.a * m.d - m.b * m.c;
  const ia = m.d / det;
  const ib = -m.b / det;
  const ic = -m.c / det;
  const id = m.a / det;
  return {
    a: ia,
    b: ib,
    c: ic,
    d: id,
    e: -(ia * m.e + ic * m.f),
    f: -(ib * m.e + id * m.f),
  };
}
